import React, { useSyncExternalStore, useEffect } from "react";
import { FiCheck, FiX, FiAlertTriangle, FiInfo } from "react-icons/fi";
import "./AppModal.css";

/* ------------------------------------------------------------------ */
/*  AppModal                                                          */
/*                                                                    */
/*  A centered, in-design-language modal that replaces react-hot-     */
/*  toast across the app. It exposes the same imperative surface as   */
/*  the old `toast` object (`toast.success`, `toast.error`, a bare    */
/*  `toast(...)` call, etc.) so existing call sites keep working —    */
/*  only the import path changes.                                     */
/*                                                                    */
/*  Mount <AppModalHost /> once (done in App.tsx).                    */
/* ------------------------------------------------------------------ */

export type AppModalType = "success" | "error" | "warning" | "info";

export interface AppModalItem {
  id: string;
  type: AppModalType;
  message: React.ReactNode;
  title?: string;
}

type Listener = () => void;

let modals: AppModalItem[] = [];
const listeners = new Set<Listener>();
let counter = 0;

const emit = () => {
  listeners.forEach((l) => l());
};

const subscribe = (listener: Listener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => modals;

const nextId = () => {
  counter += 1;
  return `apm-${counter}`;
};

const normalizeMessage = (message: unknown): React.ReactNode => {
  if (message === null || message === undefined) return "";
  if (typeof message === "string" || React.isValidElement(message)) {
    return message as React.ReactNode;
  }
  if (typeof message === "object") {
    const anyMsg = message as any;
    if (typeof anyMsg.message === "string") return anyMsg.message;
    try {
      return JSON.stringify(anyMsg);
    } catch {
      return String(anyMsg);
    }
  }
  return String(message);
};

const show = (type: AppModalType, message: unknown, title?: string): string => {
  const id = nextId();
  modals = [...modals, { id, type, message: normalizeMessage(message), title }];
  emit();
  return id;
};

export const dismiss = (id?: string) => {
  modals = id ? modals.filter((m) => m.id !== id) : [];
  emit();
};

/* Drop-in replacement for the react-hot-toast `toast` object. */
type AppToast = {
  (message?: unknown, opts?: unknown): string;
  success: (message?: unknown, opts?: unknown) => string;
  error: (message?: unknown, opts?: unknown) => string;
  warning: (message?: unknown, opts?: unknown) => string;
  warn: (message?: unknown, opts?: unknown) => string;
  info: (message?: unknown, opts?: unknown) => string;
  /* Legacy react-hot-toast API kept as safe no-ops so nothing breaks. */
  loading: (message?: unknown, opts?: unknown) => string;
  custom: (message?: unknown, opts?: unknown) => string;
  dismiss: (id?: string) => void;
  remove: (id?: string) => void;
  promise: <T>(promise: Promise<T>, msgs?: any) => Promise<T>;
};

const toast = ((message?: unknown) => show("info", message)) as AppToast;
toast.success = (message?: unknown) => show("success", message);
toast.error = (message?: unknown) => show("error", message);
toast.warning = (message?: unknown) => show("warning", message);
toast.warn = (message?: unknown) => show("warning", message);
toast.info = (message?: unknown) => show("info", message);
toast.loading = (message?: unknown) => show("info", message);
toast.custom = (message?: unknown) => show("info", message);
toast.dismiss = (id?: string) => dismiss(id);
toast.remove = (id?: string) => dismiss(id);
toast.promise = <T,>(promise: Promise<T>) => promise;

export { toast };
export default toast;

/* ------------------------------------------------------------------ */
/*  Presentation                                                      */
/* ------------------------------------------------------------------ */

const META: Record<
  AppModalType,
  { title: string; icon: React.ReactNode }
> = {
  success: { title: "Success", icon: <FiCheck size={28} /> },
  error: { title: "Error", icon: <FiX size={28} /> },
  warning: { title: "Warning", icon: <FiAlertTriangle size={26} /> },
  info: { title: "Information", icon: <FiInfo size={28} /> },
};

const AppModalCard = ({ item, onClose }: { item: AppModalItem; onClose: () => void }) => {
  const meta = META[item.type];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "Enter") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="apm-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="apm-card" role="alertdialog" aria-modal="true">
        <span className="apm-close" onClick={onClose} aria-label="Close">
          <FiX size={18} />
        </span>
        <div className={`apm-icon apm-icon--${item.type}`}>{meta.icon}</div>
        <p className={`apm-title apm-title--${item.type}`}>{item.title || meta.title}</p>
        <div className="apm-message">{item.message}</div>
        <div className="apm-actions">
          <button type="button" className="apm-btn apm-btn--primary" onClick={onClose} autoFocus>
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

export const AppModalHost = () => {
  const items = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  if (!items.length) return null;
  /* Only the most recent modal is shown; dismissing it reveals the one beneath. */
  const current = items[items.length - 1];
  return <AppModalCard item={current} onClose={() => dismiss(current.id)} />;
};
